/**
 * LenderDemoAccess — passcode gate for the freely-explorable LENDER demo.
 * Route: /lender-demo
 * Enters the seeded lender account (read-only) and lands on /lender.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { Lock, ArrowRight, Landmark } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const NAVY = '#0b3b6f';

export default function LenderDemoAccess() {
  const [passcode, setPasscode] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  async function enter(e) {
    e?.preventDefault?.();
    setError(null);
    if (!passcode.trim()) { setError('Enter the demo passcode.'); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/lender-demo/access`, { passcode: passcode.trim() });
      const userToStore = { ...data };
      delete userToStore.show_feature_announcement;
      delete userToStore.current_feature_version;
      localStorage.setItem('user', JSON.stringify(userToStore));
      localStorage.setItem('demo_session', 'lender'); // suppresses What's-New modal for the pitch
      window.location.href = '/lender';
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Unable to open the demo. Please try again.');
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-2.5 mb-6">
          <div className="w-11 h-11 flex items-center justify-center font-extrabold text-white text-xl" style={{ background: '#2563EB', borderRadius: 12 }}>D</div>
          <div className="text-left">
            <div className="font-extrabold text-slate-900 text-lg leading-tight">DrawReady</div>
            <div className="text-xs text-slate-400 font-medium">Construction Finance</div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-7">
          <div className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-blue-600 bg-blue-50 border border-blue-100 px-2.5 py-1 rounded mb-3">
            <Landmark className="w-3.5 h-3.5" /> Lender Demo Access
          </div>
          <h1 className="text-2xl font-extrabold text-slate-900">Explore the lender portfolio</h1>
          <p className="text-sm text-slate-500 mt-1">
            See a realistic portfolio of 10 borrowers and their construction loans — draws, interest, compliance,
            completion photos and signed lien waivers. Read-only; explore freely.
          </p>

          <form onSubmit={enter} className="mt-5">
            <label className="text-xs font-bold uppercase tracking-wide text-slate-500">Demo passcode</label>
            <div className="mt-1.5 flex items-center gap-2 border border-slate-300 rounded-lg px-3 h-12 bg-slate-50 focus-within:border-blue-500">
              <Lock className="w-4 h-4 text-slate-400" />
              <input value={passcode} onChange={(e) => setPasscode(e.target.value)} placeholder="Enter passcode"
                className="flex-1 bg-transparent outline-none text-sm" data-testid="lender-demo-passcode" autoFocus />
            </div>
            {error && <p className="text-sm text-red-600 mt-2" data-testid="lender-demo-error">{error}</p>}
            <button type="submit" disabled={loading}
              className="mt-4 w-full h-12 rounded-lg text-white font-bold inline-flex items-center justify-center gap-2 disabled:opacity-60"
              style={{ background: NAVY }} data-testid="lender-demo-enter">
              {loading ? 'Opening…' : <>Enter demo <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>
        </div>
        <p className="text-center text-[11px] text-slate-400 mt-4">Sample data only — no real borrower information.</p>
      </div>
    </div>
  );
}
